import { WSCard, WSElement, WSText } from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { ClientWorkFlowStatus } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { useQueryPayables } from "../../../query/payments/queries";
import { WSQueries } from "../../../query/WSQuery";
import {
  PayablesFilters,
  PayablesStatusFilter,
  PayablesTable,
  usePayablesQuery
} from "../../components/NewPayablesTable/PayablesTable";
import { useDefaultCurrency } from "../../../shared/hooks/useDefaultCurrency";
import { API_QUERY_PAYABLES_DUAL_STAGE_TO_PREAPPROVE } from "../../utils/payables/apiQueries";
import { useDualStagePreapproveAll } from "../../utils/payables/approveAll";
import { useDualStagePreapproveAllBulkAction } from "../../utils/payables/approveAllBulkAction";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";

const DEFAULT_FILTERS: PayablesFilters = {
  status: [PayablesStatusFilter.ToApprove],
  custom: {
    "client.workflowStatus": {
      notIn: [
        ClientWorkFlowStatus.PreApproved,
        ClientWorkFlowStatus.Approved,
        ClientWorkFlowStatus.PaymentInitiated,
        ClientWorkFlowStatus.Funded
      ]
    }
  }
};

export const PayablesDualStageToPreApprove: React.FC = () => {
  useBrowserPageTitle("Payables To Pre-Approve");
  const history = useHistory();
  const defaultCurrency = useDefaultCurrency();
  const featureFlags = useFeatureFlags();

  const draftPayablesQuery = usePayablesQuery({
    status: [PayablesStatusFilter.Draft]
  });

  const queryPayablesToPreApprove = useQueryPayables({
    ...API_QUERY_PAYABLES_DUAL_STAGE_TO_PREAPPROVE,
    page: {
      size: 1
    }
  });

  const legacyPreApproveAll = useDualStagePreapproveAll();
  const preApproveAllBulkAction = useDualStagePreapproveAllBulkAction();

  const isBulkActions = featureFlags.data?.bulkPayableActions;

  return (
    <>
      <WSText.Heading1>To Pre-Approve</WSText.Heading1>
      <WSText color="gray600" mb="2XL">
        All payables and requests that are not yet scheduled to be paid.
      </WSText>

      <WSElement mb="2XL">
        <WSQueries
          queries={{ draftPayablesQuery, queryPayablesToPreApprove }}
          renderErrors={() => null}
        >
          {({
            draftPayablesQuery: {
              data: { summary: draftPayablesSummary }
            },
            queryPayablesToPreApprove: {
              data: { summary: toPreApprovePayablesSummary }
            }
          }) => (
            <>
              {toPreApprovePayablesSummary.listSize > 1 && (
                <WSCard
                  mb="XL"
                  header={{
                    label: {
                      avatar: {
                        type: "icon",
                        icon: "dollar-circle",
                        size: "M",
                        color: "amber400",
                        colorBackground: "amber50"
                      },
                      text: "Pending pre-approval:"
                    }
                  }}
                  divider="bottom"
                  actions={{
                    alignment: "left",
                    buttons: [
                      {
                        label: `Pre-approve ${toPreApprovePayablesSummary.listSize} payments`,
                        kind: "Link",
                        rightIcon: "chevron-right",
                        onClick: isBulkActions
                          ? () =>
                              preApproveAllBulkAction(
                                toPreApprovePayablesSummary.listSize
                              )
                          : legacyPreApproveAll.act,
                        loading: isBulkActions
                          ? undefined
                          : legacyPreApproveAll.isLoading
                      }
                    ]
                  }}
                >
                  <WSText.ParagraphSm color="gray500">
                    {toPreApprovePayablesSummary.listSize} payables totaling{" "}
                    {formatMoney(
                      toPreApprovePayablesSummary.totalValue,
                      defaultCurrency
                    )}
                  </WSText.ParagraphSm>
                </WSCard>
              )}

              {draftPayablesSummary.listSize > 0 && (
                <WSCard
                  header={{
                    label: {
                      avatar: {
                        type: "icon",
                        icon: "alert-circle",
                        size: "M",
                        color: "amber400",
                        colorBackground: "amber50"
                      },
                      text: "Draft payables"
                    }
                  }}
                  divider="bottom"
                  actions={{
                    alignment: "left",
                    buttons: [
                      {
                        label: "Manage draft payables",
                        kind: "Link",
                        rightIcon: "chevron-right",
                        onClick: () => {
                          history.push("/member/invoices/payables/draft");
                        }
                      }
                    ]
                  }}
                >
                  <WSText.ParagraphSm color="gray500">
                    {draftPayablesSummary.listSize} draft payable
                    {draftPayablesSummary.listSize > 1 ? "s" : ""}.
                  </WSText.ParagraphSm>
                </WSCard>
              )}
            </>
          )}
        </WSQueries>
      </WSElement>

      <PayablesTable
        bulkActions={["preapprove", "delete"]}
        visibleFilters={{
          payee: true,
          collaboratorReviewStatus: true,
          openedAtRange: true,
          dueDateRange: true
        }}
        basePath="/member/invoices/payables/to-preapprove"
        defaultFilters={DEFAULT_FILTERS}
        showHeaders
        showInvoiceNumber
        showDateRequested
        showDueDate
        emptyState={{
          firstTime:
            "Every requested, scheduled, and paid payable will display here.",
          standart:
            "You're all caught up! Nothing is waiting for your pre-approval."
        }}
      />
    </>
  );
};
